<script>
import NameBubble from '../../../components/name-bubble.vue';

export default {
    name: 'Comment',

    props: {
        message: {
            type: Object,
            required: true
        },
        
        newComment: {
            type: Object,
            required: true
        },

        updatingComment: {
            type: Object,
            required: true
        },
        
        replyingComment: {
            type: Object,
            required: true
        },
        
        deletingComment: {
            type: Object,
            required: true
        },

        depth: {
            type: Number,
            required: true
        },

        customStyle: {
            default: () => {
                return {
                    'textarea': {},
                    'date-update-data': {},
                };
            },
            type: Object,
        },
    },

    components: {
        NameBubble
    },

    data() {
        return {
            AVATAR_RADIUS: 30,

            bottomOptions: [
                { key: 'share', icon: 'fa-solid fa-share-nodes', color: 'var(--icon-main)' },
                { key: 'reply', icon: 'fa-solid fa-reply', color: 'var(--sustainfinity-faded-green)' },
                { key: 'update', icon: 'fa-solid fa-pen-to-square', color: 'var(--sustainfinity-blue)' },
                { key: 'delete', icon: 'fa-solid fa-trash', color: 'var(--invalid-input)' },
            ],
        }
    },

    methods: {
        getAvatarWords(name) {
            return name.split(' ');
        },

        handleTextareaKeydown(e, action) {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                this.$emit('action', action);
            }
        },

        handleBottomButtonClick(key, comment) {
            if (key === 'share') {
                this.copyCommentLink(comment);
            } else if (key === 'reply') {
                this.$emit('reply', comment);
            } else if (key === 'update') {
                this.$emit('update', comment);
            } else if (key === 'delete') {
                this.$emit('delete', comment);
            }
        },

        copyCommentLink(_comment) {
            const query = { ...this.$route.query, comment: _comment.id };
            this.$router.replace({ query });
            navigator.clipboard.writeText(window.location.origin + this.$router.resolve({ query }).href);
            this.$notify('info', this.$t('metric_discussions.copied'), true);
        }
    }
}
</script>

<template>
    <div class="comment"
        :style="{
            'padding-left': `calc(${depth * 30}px + .75em)`,
            'border-bottom': depth === 0 ? 'thin solid lightgray' : null
        }"
    >
        <div class="top-row child-div">
            <name-bubble 
                class="avatar" 
                :Words="getAvatarWords(message.sender)" 
                :XonHover="false"
                :Radius="AVATAR_RADIUS" 
                :PutEmphasis="true" 
            />

            <div class="meta">
                <div class="sender">
                    {{ message.sender }}
                </div>
                <div class="date" :style="customStyle['date-update-data']">
                    {{ message.created_on | dateFormat }}
                </div>
                <div v-if="message.editor != null" class="update-data" :style="customStyle['date-update-data']">
                    {{ $t('metric_discussions.edited') }}: {{ message.editor }} - <span>{{ message.updated_on | dateFormat }}</span>
                </div>
            </div>
        </div>

        <div class="text child-div">
            <div v-if="updatingComment && updatingComment.id === message.id"
                class="add-comment"
            >
                <textarea 
                    :style="customStyle['textarea']"
                    v-model="updatingComment.text" 
                    name="updatingComment" id="updatingComment" rows="3"
                    @keydown="handleTextareaKeydown($event, 'update')"
                ></textarea>

                <div class="buttons">
                    <button class="sust-btn sust-btn-green" @click="$emit('action', 'update')">
                        {{ $t('metric_discussions.buttons.save') }}
                    </button>
                    <button class="icon-button" @click="$emit('cancel', 'update')"
                        :style="{
                            'color': customStyle['textarea']['color'] || null
                        }"
                    >
                        {{ $t('metric_discussions.buttons.cancel') }}
                    </button>
                </div>
            </div>
            <div v-else class="comment-text">
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span v-html="message.text"></span>
            </div>
        </div>

        <div class="bottom-row child-div" v-if="replyingComment === null">
            <button v-for="option in bottomOptions" :key="option.key" class="icon-button"
                @click="handleBottomButtonClick(option.key, message)">
                <i :class="option.icon" 
                    :style="{ 'color': option.color }"
                    v-sust-tooltip="{
                        text: $t(`metric_discussions.bottom_options.${option.key}`), 
                        fontSize: '.75em'
                    }"
                ></i>
            </button>

            <div v-if="deletingComment && deletingComment.id === message.id"
                class="delete-warning"
            >
                <div>
                    {{ $t('metric_discussions.delete_warning') }}
                </div>
                <div class="buttons">
                    <button class="sust-btn sust-btn-red" @click="$emit('action', 'delete')">
                        {{ $t('metric_discussions.buttons.delete') }}
                    </button>
                    <button class="icon-button" @click="$emit('cancel', 'delete')">
                        {{ $t('metric_discussions.buttons.cancel') }}
                    </button>
                </div>
            </div>
        </div>

        <div v-if="replyingComment && replyingComment.id === message.id"
            class="flex-all-centered-col child-div" 
            :style="{'padding-left': `calc(${(depth+1) * 30}px + .75em)`}"
        >
            <div class="add-comment">
                <textarea 
                    :style="customStyle['textarea']"
                    v-model="newComment.text" 
                    name="replyingComment" id="replyingComment" rows="3"
                    @keydown="handleTextareaKeydown($event, 'create')"
                ></textarea>
    
                <div class="buttons">
                    <button class="sust-btn sust-btn-green" @click="$emit('action', 'create')">
                        {{ $t('metric_discussions.buttons.save') }}
                    </button>
                    <button class="icon-button" @click="$emit('cancel', 'reply')"
                        :style="{
                            'color': customStyle['textarea']['color'] || null
                        }"
                    >
                        {{ $t('metric_discussions.buttons.cancel') }}
                    </button>
                </div>
            </div>
        </div>

        <comment v-for="reply in message.replies" :key="reply.id" 
            :id="`comment_id_${reply.id}`"
            :message="reply"
            :newComment="newComment"
            :updatingComment="updatingComment"
            :replyingComment="replyingComment"
            :deletingComment="deletingComment"
            :depth="depth + 1"
            :customStyle="customStyle"
            @action="$emit('action', $event)"
            @cancel="$emit('cancel', $event)"
            @reply="$emit('reply', $event)"
            @update="$emit('update', $event)"
            @delete="$emit('delete', $event)"
        />
    </div>
</template>

<style lang='scss' scoped>
.add-comment {
    width: 100%;
    margin: 2em 0 .5em 0;
    min-height: 4em;

    > textarea {
        padding: .5em .75em;
        background-color: transparent;
        border: 0 !important;
        outline: thin solid lightgray;
        color: white;
        width: 100%;
        border-radius: .25em;
    }

    > .buttons {
        display: flex;
        max-height: 2em;

        > button {
            margin-right: .5em;
            box-shadow: none !important;
        }
    }

}

.comment {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    padding: 1em 0;

    &:last-of-type {
        border-bottom: 0 !important;
    }

    > .child-div {
        width: 100%;
        padding-bottom: .3em;

        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        text-align: left;
    }

    > .top-row {
        font-size: 1em;

        > .avatar {
            margin-right: .75em;
        }

        > .meta {
            display: flex;
            flex-direction: column;

            > .sender {
                font-weight: bolder;
            }

            > .date, .update-data {
                font-size: .7em;
                font-weight: lighter;
                color: lightgray;
            }
        }
    }

    > .text {
        padding-bottom: 1em;
        padding-top: 1em;

        > .comment-text {
            overflow-wrap: break-word;
            word-break: break-word;
            white-space: pre-wrap;
        }

        > .add-comment {
            margin: 0 0 1em 0 !important;
        }
    }

    > .bottom-row {
        font-size: .9em;
        font-weight: lighter;
        color: lightgray;

        position: relative;
        > .delete-warning {
            position: absolute;
            z-index: 99;
            display: flex;
            flex-direction: column;
            padding: 1em;
            background-color: var(--carbon);
            border: thin solid var(--carbon-fade);
            border-radius: .25em;

            > .buttons {
                display: flex;
                margin-top: .5em;

                > button {
                    margin-right: .5em;
                    box-shadow: none !important;
                }
            }
        }
    }
}

.icon-button {
    padding: 0 !important;
    margin-right: .5em;
    color: whitesmoke;

    &:hover {
        color: lightgray;
    }
}

.flash {
    animation: flash 1s ease;
}

@keyframes flash {
    0% { 
        background-color: lightgray;
        color: black;
    } 100% { 
        background-color: inherit; 
        color: inherit;
    }
}
</style>